import { useState, useEffect } from 'react';
import { Package, Loader2, ExternalLink, Mail, RefreshCw, Calendar, CreditCard } from 'lucide-react';
import { getSubscriptions, createPortalSession, type Subscription } from '../../services/checkout/subscriptionService';
import { getCurrentPatientAuth } from '../../services/auth/authUpgradeService';
import { PRODUCT_NAME } from '../../config/brand';

function formatDate(timestamp: number): string {
  return new Date(timestamp * 1000).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatAmount(amount: number | null, currency: string): string {
  if (amount === null) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amount / 100);
}

function statusLabel(status: string, cancelAtPeriodEnd: boolean) {
  if (cancelAtPeriodEnd) return { text: 'Canceling', color: 'bg-amber-100 text-amber-700' };
  switch (status) {
    case 'active':
      return { text: 'Active', color: 'bg-green-100 text-green-700' };
    case 'past_due':
      return { text: 'Past Due', color: 'bg-red-100 text-red-700' };
    case 'canceled':
      return { text: 'Canceled', color: 'bg-zinc-100 text-zinc-500' };
    case 'paused':
      return { text: 'Paused', color: 'bg-blue-100 text-blue-700' };
    default:
      return { text: status, color: 'bg-zinc-100 text-zinc-500' };
  }
}

export function SubscriptionsPage() {
  const [email, setEmail] = useState('');
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);

  // Auto-detect email from auth
  useEffect(() => {
    const auth = getCurrentPatientAuth();
    if (auth.isAuthenticated && auth.email) {
      setEmail(auth.email);
    }
  }, []);

  const handleLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    setError(null);
    setSearched(true);

    try {
      const subs = await getSubscriptions(email.trim());
      setSubscriptions(subs);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const handleManage = async () => {
    setPortalLoading(true);
    setError(null);

    try {
      const url = await createPortalSession(email.trim());
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open subscription manager');
      setPortalLoading(false);
    }
  };

  const activeSubs = subscriptions.filter(
    (s) => ['active', 'past_due', 'trialing'].includes(s.status) || s.cancelAtPeriodEnd,
  );
  const pastSubs = subscriptions.filter(
    (s) => ['canceled', 'incomplete_expired'].includes(s.status) && !s.cancelAtPeriodEnd,
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#18181B]">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-zinc-200 px-4 py-3">
        <div className="max-w-lg mx-auto">
          <h1 className="font-['Instrument_Serif'] text-lg font-bold">My Subscriptions</h1>
          <p className="text-zinc-500 text-[13px] font-['DM_Sans']">
            Manage your {PRODUCT_NAME} supplement subscriptions
          </p>
        </div>
      </div>

      <div className="px-4 py-6 max-w-lg mx-auto font-['DM_Sans']">
        {/* Email lookup form */}
        <form onSubmit={handleLookup} className="bg-white border border-zinc-200 rounded-[4px] p-4 mb-6">
          <label htmlFor="sub-email" className="text-[13px] font-medium text-zinc-600 block mb-2">
            Enter your email to view subscriptions
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Mail
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none"
              />
              <input
                id="sub-email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 text-[14px] rounded-[4px] border border-zinc-200 bg-white text-[#18181B] placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#E11D48]/30 focus:border-[#E11D48] transition-colors"
              />
            </div>
            <button
              type="submit"
              disabled={loading || !email.trim()}
              className="flex items-center gap-1.5 px-4 py-2.5 bg-[#E11D48] text-white font-medium text-[14px] rounded-[4px] hover:bg-[#BE123C] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={14} />}
              Look Up
            </button>
          </div>
        </form>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-[4px] p-3 mb-4">
            <p className="text-red-600 text-[13px]">{error}</p>
          </div>
        )}

        {/* Results */}
        {searched && !loading && (
          <>
            {activeSubs.length > 0 ? (
              <div className="space-y-4 mb-6">
                <h2 className="font-['Instrument_Serif'] text-base font-bold">Active Subscriptions</h2>
                {activeSubs.map((sub) => {
                  const badge = statusLabel(sub.status, sub.cancelAtPeriodEnd);
                  const mainItem = sub.items[0];
                  return (
                    <div key={sub.id} className="bg-white border border-zinc-200 rounded-[4px] p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Package size={16} className="text-[#E11D48]" />
                          <h3 className="text-[14px] font-bold">
                            {mainItem?.productName || `${PRODUCT_NAME} Protocol`}
                          </h3>
                        </div>
                        <span
                          className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${badge.color}`}
                        >
                          {badge.text}
                        </span>
                      </div>

                      <div className="space-y-2 mb-3">
                        <div className="flex items-center gap-2 text-[13px] text-zinc-600">
                          <CreditCard size={13} className="text-zinc-400" />
                          <span>
                            {mainItem
                              ? `${formatAmount(mainItem.amount, mainItem.currency)}/${mainItem.interval}`
                              : 'N/A'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-[13px] text-zinc-600">
                          <Calendar size={13} className="text-zinc-400" />
                          <span>Next billing: {formatDate(sub.currentPeriodEnd)}</span>
                        </div>
                        {sub.cancelAtPeriodEnd && (
                          <p className="text-amber-600 text-[13px]">
                            Cancels on {formatDate(sub.currentPeriodEnd)}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Manage button */}
                <button
                  onClick={handleManage}
                  disabled={portalLoading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#E11D48] text-white font-bold text-[15px] rounded-[4px] hover:bg-[#BE123C] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {portalLoading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Opening...
                    </>
                  ) : (
                    <>
                      <ExternalLink size={16} />
                      Manage Subscriptions
                    </>
                  )}
                </button>
                <p className="text-zinc-400 text-[12px] text-center">
                  Cancel, pause, or update payment method via Stripe's secure portal
                </p>
              </div>
            ) : (
              <div className="bg-white border border-zinc-200 rounded-[4px] p-6 text-center mb-6">
                <Package size={24} className="text-zinc-300 mx-auto mb-2" />
                <p className="text-zinc-500 text-[14px]">No active subscriptions found</p>
                <p className="text-zinc-400 text-[13px] mt-1">
                  Make sure you're using the email from your order
                </p>
              </div>
            )}

            {/* Past subscriptions */}
            {pastSubs.length > 0 && (
              <div className="space-y-3">
                <h2 className="font-['Instrument_Serif'] text-base font-bold text-zinc-400">
                  Past Subscriptions
                </h2>
                {pastSubs.map((sub) => {
                  const mainItem = sub.items[0];
                  return (
                    <div key={sub.id} className="bg-white border border-zinc-100 rounded-[4px] p-3 opacity-60">
                      <div className="flex items-center justify-between">
                        <span className="text-[13px] text-zinc-500">
                          {mainItem?.productName || 'Protocol'}
                        </span>
                        <span className="text-[11px] text-zinc-400">
                          Canceled {sub.canceledAt ? formatDate(sub.canceledAt) : ''}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
